import {
  Add,
  CheckCircle,
  Cloud,
  Delete,
  Email,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import {
  CalendarProvider,
  ICalendarConnectionConfig,
  IDiscoveredCalendar,
  IUserCalendarConnection,
} from '../Interfaces/IUserCalendarConnection';
import {
  useCalendarConnections,
  useCreateCalendarConnectionsBatch,
  useDeleteCalendarConnection,
  useDiscoverCalendars,
  useDiscoverO365Calendars,
  useGetO365AuthUrl,
  useTestCalendarConnection,
  useUpdateCalendarConnection,
} from '../hooks/reactQuerys/CalendarConnectionQueries';
import { Checkbox, FormControlLabel } from '@mui/material';

interface IProps {
  userId: number;
  /** Callback nach erfolgreichem Hinzufügen einer Connection — wird vom
   *  CalendarMain genutzt, um die Event-Liste neu zu laden. */
  onConnectionsChanged?: () => void;
  /** Wenn `true`, wird der Add-Dialog beim Mount sofort geöffnet — damit
   *  der „Kalender hinzufügen"-Button in der Sidebar direkt zur Eingabe
   *  springt, statt den Umweg über die Verwaltungs-Liste zu nehmen. */
  initialAddOpen?: boolean;
  /** Wird gerufen, wenn der Add-Dialog geschlossen wird (Abbruch oder X).
   *  Erlaubt dem Aufrufer, die ConnectionsView danach komplett zu
   *  unmounten — wichtig fuer den „Headless-Modus" aus CalendarMain. */
  onAddDialogClose?: () => void;
}

const PROVIDER_ICON: Record<CalendarProvider, React.ReactNode> = {
  o365: <Email fontSize='small' />,
  webdav: <Cloud fontSize='small' />,
};

const PROVIDER_LABEL: Record<CalendarProvider, string> = {
  o365: 'Microsoft 365',
  webdav: 'WebDAV / CalDAV',
};

export const CalendarConnectionsView: React.FC<IProps> = ({
  userId,
  onConnectionsChanged,
  initialAddOpen,
  onAddDialogClose,
}) => {
  const { connections, isLoadingConnections, refetchConnections } =
    useCalendarConnections(userId);
  const { updateConnection } = useUpdateCalendarConnection(userId);
  const { deleteConnection } = useDeleteCalendarConnection(userId);
  const { testConnection, isTestingConnection } = useTestCalendarConnection();
  const [addOpen, setAddOpen] = useState(Boolean(initialAddOpen));

  const [testResult, setTestResult] = useState<{
    id: number;
    ok: boolean;
    message: string;
  } | null>(null);

  const handleSetDefault = async (conn: IUserCalendarConnection) => {
    await updateConnection({
      idUserCalendarConnection: conn.idUserCalendarConnection,
      patch: { IsDefault: !conn.IsDefault },
    });
    onConnectionsChanged?.();
  };

  const handleToggleActive = async (conn: IUserCalendarConnection) => {
    await updateConnection({
      idUserCalendarConnection: conn.idUserCalendarConnection,
      patch: { IsActive: !conn.IsActive },
    });
    onConnectionsChanged?.();
  };

  const handleDelete = async (conn: IUserCalendarConnection) => {
    if (
      !window.confirm(
        `Verbindung "${conn.Label}" wirklich entfernen? Bei O365 wird auch der hinterlegte OAuth-Token gelöscht.`
      )
    )
      return;
    await deleteConnection(conn.idUserCalendarConnection);
    onConnectionsChanged?.();
  };

  const handleTest = async (conn: IUserCalendarConnection) => {
    const result = await testConnection(conn.idUserCalendarConnection);
    setTestResult({
      id: conn.idUserCalendarConnection,
      ok: result.ok,
      message: result.message,
    });
  };

  if (isLoadingConnections) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const empty = connections.length === 0;

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', mt: 3 }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2 }}
      >
        <Typography variant='h6'>Kalenderverbindungen</Typography>
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={() => setAddOpen(true)}
        >
          Kalender hinzufügen
        </Button>
      </Stack>

      {empty ? (
        <Alert severity='info' sx={{ mb: 2 }}>
          Noch keine Kalenderverbindung hinterlegt. Klicken Sie oben rechts auf
          „Kalender hinzufügen", um sich mit Microsoft 365 oder einem
          WebDAV/CalDAV-Anbieter zu verbinden.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {connections.map(conn => {
            const url = conn.Config?.url as string | undefined;
            const email = conn.Config?.email as string | undefined;
            const result =
              testResult?.id === conn.idUserCalendarConnection
                ? testResult
                : null;
            return (
              <Box
                key={conn.idUserCalendarConnection}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  columnGap: 2,
                  rowGap: 1,
                  alignItems: 'flex-start',
                  bgcolor: conn.IsActive ? 'background.paper' : 'action.hover',
                }}
              >
                {/* Spalte 1: Provider-Icon */}
                <Box sx={{ pt: 0.5, color: 'text.secondary' }}>
                  {PROVIDER_ICON[conn.Provider]}
                </Box>

                {/* Spalte 2: Text-Block (Label, Chips, Detail-URL) */}
                <Box sx={{ minWidth: 0 /* erlaubt Truncation */ }}>
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    sx={{ flexWrap: 'wrap', mb: 0.5 }}
                  >
                    <Typography
                      variant='body1'
                      sx={{ fontWeight: 500 }}
                      title={conn.Label}
                    >
                      {conn.Label}
                    </Typography>
                    <Chip
                      size='small'
                      label={PROVIDER_LABEL[conn.Provider]}
                      variant='outlined'
                    />
                    {conn.IsDefault && (
                      <Chip size='small' color='warning' label='Standard' />
                    )}
                    {!conn.IsActive && <Chip size='small' label='Inaktiv' />}
                  </Stack>

                  {email && (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={email}
                    >
                      {email}
                    </Typography>
                  )}
                  {url && (
                    <Tooltip title={url} placement='bottom-start'>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontFamily: 'monospace',
                        }}
                      >
                        {url}
                      </Typography>
                    </Tooltip>
                  )}

                  {result && (
                    <Alert
                      severity={result.ok ? 'success' : 'error'}
                      sx={{ mt: 1 }}
                      icon={
                        result.ok ? <CheckCircle fontSize='small' /> : undefined
                      }
                    >
                      {result.message}
                    </Alert>
                  )}
                </Box>

                {/* Spalte 3: Aktionen, klar abgesetzt */}
                <Stack
                  direction='row'
                  spacing={0.5}
                  alignItems='center'
                  sx={{ flexShrink: 0 }}
                >
                  <Tooltip
                    title={
                      conn.IsDefault
                        ? 'Standardkalender — Klick zum Aufheben'
                        : 'Als Standardkalender setzen'
                    }
                  >
                    <IconButton
                      size='small'
                      onClick={() => handleSetDefault(conn)}
                    >
                      {conn.IsDefault ? (
                        <Star color='warning' fontSize='small' />
                      ) : (
                        <StarBorder fontSize='small' />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='Verbindung testen'>
                    <span>
                      <Button
                        size='small'
                        onClick={() => handleTest(conn)}
                        disabled={isTestingConnection}
                      >
                        Testen
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={conn.IsActive ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    <Button
                      size='small'
                      onClick={() => handleToggleActive(conn)}
                    >
                      {conn.IsActive ? 'Aktiv' : 'Inaktiv'}
                    </Button>
                  </Tooltip>
                  <Tooltip title='Verbindung löschen'>
                    <IconButton
                      size='small'
                      color='error'
                      onClick={() => handleDelete(conn)}
                    >
                      <Delete fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}

      {addOpen && (
        <AddConnectionDialog
          userId={userId}
          onClose={() => {
            setAddOpen(false);
            onAddDialogClose?.();
          }}
          onCreated={() => {
            setAddOpen(false);
            void refetchConnections();
            onConnectionsChanged?.();
          }}
        />
      )}
    </Box>
  );
};

interface IAddProps {
  userId: number;
  onClose: () => void;
  onCreated: () => void;
}

/** Phase im O365-Add-Flow. Der User durchlaeuft sie sequenziell:
 *   `intro` → Erklaerung + Anmelde-Button
 *   `login` → Microsoft-Popup ist offen, wir warten auf das postMessage
 *             des `calenderauth`-Callbacks
 *   `choose` → Discover hat die Kalender geliefert, User waehlt aus
 */
type O365Phase = 'intro' | 'login' | 'choose';

const AddConnectionDialog: React.FC<IAddProps> = ({
  userId,
  onClose,
  onCreated,
}) => {
  const [provider, setProvider] = useState<CalendarProvider>('o365');
  const [config, setConfig] = useState<ICalendarConnectionConfig>({});
  const [error, setError] = useState<string | null>(null);

  // WebDAV-Workflow: URL+Login → Discover → Auswahl → Save.
  // O365-Workflow: Anmelden → (postMessage) → Discover → Auswahl → Save.
  const [discovered, setDiscovered] = useState<IDiscoveredCalendar[] | null>(
    null
  );
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [o365Phase, setO365Phase] = useState<O365Phase>('intro');

  const { discoverCalendars, isDiscovering } = useDiscoverCalendars();
  const { discoverO365Calendars, isDiscoveringO365 } =
    useDiscoverO365Calendars();
  const { getO365AuthUrl, isLoadingO365AuthUrl } = useGetO365AuthUrl();
  const { createMany, isCreatingMany } =
    useCreateCalendarConnectionsBatch(userId);

  // Referenz auf das geoeffnete OAuth-Popup, damit wir beim Dialog-Close
  // hinterherraeumen koennen.
  const popupRef = useRef<Window | null>(null);

  const handleProviderChange = (newProvider: CalendarProvider) => {
    setProvider(newProvider);
    setConfig({});
    setDiscovered(null);
    setSelected({});
    setO365Phase('intro');
    setError(null);
  };

  const _calKey = (c: IDiscoveredCalendar) => c.id || c.url;

  const runO365Discover = async () => {
    try {
      const result = await discoverO365Calendars(userId);
      if (!result.ok) {
        setError(
          result.message ||
            'Kalender konnten nach der Anmeldung nicht geladen werden.'
        );
        setO365Phase('intro');
        return;
      }
      setDiscovered(result.calendars);
      const initialSel: Record<string, boolean> = {};
      for (const c of result.calendars) {
        if (c.isDefault) initialSel[_calKey(c)] = true;
      }
      // Wenn kein Default gefunden wurde: alle vorausgewaehlt.
      if (!Object.values(initialSel).some(Boolean)) {
        for (const c of result.calendars) initialSel[_calKey(c)] = true;
      }
      setSelected(initialSel);
      setO365Phase('choose');
      if (result.calendars.length === 0) {
        setError(
          'Microsoft hat keine Kalender zurückgeliefert. Bitte prüfen Sie die Berechtigungen der DiNo-App in Ihrem Microsoft-Konto.'
        );
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Kalender-Suche fehlgeschlagen.'
      );
      setO365Phase('intro');
    }
  };

  // Listener fuer das postMessage des Microsoft-Callback-Popups. Sobald wir
  // 'success' empfangen, starten wir den Discover und schalten in die
  // Auswahl-Phase.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const data = event.data as
        | { source?: string; status?: string; message?: string }
        | undefined;
      if (!data || data.source !== 'dino-o365-auth') return;
      if (data.status === 'success') {
        setError(null);
        void runO365Discover();
      } else {
        setError(
          data.message ||
            'Microsoft-Anmeldung fehlgeschlagen. Bitte erneut versuchen.'
        );
        setO365Phase('intro');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [userId]);

  // Beim Schliessen des Dialogs auch das (evtl. noch offene) OAuth-Popup
  // schliessen, sonst bleibt es als Geist im Browser stehen.
  useEffect(() => {
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        try {
          popupRef.current.close();
        } catch {
          /* cross-origin nach dem MS-Redirect — nicht schliessbar, egal */
        }
      }
    };
  }, []);

  const startO365Login = async () => {
    setError(null);
    try {
      const result = await getO365AuthUrl(userId);
      if (!result.ok || !result.authUrl) {
        setError(
          result.message ||
            'Microsoft-Anmeldelink konnte nicht erstellt werden. Bitte prüfen Sie die Calendar-Erweiterung.'
        );
        return;
      }
      const popup = window.open(
        result.authUrl,
        'Microsoft Login',
        'width=520,height=640,scrollbars=yes'
      );
      if (!popup) {
        setError(
          'Popup-Blocker hat das Anmeldefenster blockiert. Bitte Popups für DiNo erlauben und erneut starten.'
        );
        return;
      }
      popupRef.current = popup;
      setO365Phase('login');
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Microsoft-Anmeldelink konnte nicht erstellt werden.'
      );
    }
  };

  const handleDiscover = async () => {
    setError(null);
    if (!config.url || !config.username || !config.password) {
      setError('Bitte URL, Benutzername und Passwort angeben.');
      return;
    }
    try {
      const result = await discoverCalendars({
        Provider: provider,
        Config: config,
      });
      setDiscovered(result.calendars);
      const initialSel: Record<string, boolean> = {};
      for (const c of result.calendars) initialSel[_calKey(c)] = true;
      setSelected(initialSel);
      if (result.calendars.length === 0) {
        setError('Keine Kalender gefunden. Bitte URL und Login prüfen.');
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Kalender-Suche fehlgeschlagen'
      );
    }
  };

  const submitO365 = async () => {
    setError(null);
    if (!discovered) return;
    const chosen = discovered.filter(c => selected[_calKey(c)]);
    if (chosen.length === 0) {
      setError('Bitte mindestens einen Kalender auswählen.');
      return;
    }
    try {
      await createMany({
        Provider: 'o365',
        Config: {},
        Calendars: chosen,
      });
      onCreated();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Verbindungen konnten nicht angelegt werden'
      );
    }
  };

  const submitWebDAV = async () => {
    setError(null);
    if (!discovered) {
      await handleDiscover();
      return;
    }
    const chosen = discovered.filter(c => selected[_calKey(c)]);
    if (chosen.length === 0) {
      setError('Bitte mindestens einen Kalender auswählen.');
      return;
    }
    try {
      await createMany({
        Provider: 'webdav',
        Config: config,
        Calendars: chosen,
      });
      onCreated();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Verbindungen konnten nicht angelegt werden'
      );
    }
  };

  const isWebDAV = provider === 'webdav';
  const submitting =
    isCreatingMany ||
    isDiscovering ||
    isDiscoveringO365 ||
    isLoadingO365AuthUrl;
  const submitLabel = (() => {
    if (provider === 'o365') {
      if (o365Phase === 'intro') {
        return isLoadingO365AuthUrl
          ? 'Anmeldelink wird erstellt…'
          : 'Mit Microsoft anmelden';
      }
      if (o365Phase === 'login') {
        return isDiscoveringO365
          ? 'Lade Kalender…'
          : 'Warte auf Microsoft-Anmeldung…';
      }
      return isCreatingMany
        ? 'Speichere…'
        : `${Object.values(selected).filter(Boolean).length} Kalender hinzufügen`;
    }
    if (!discovered) return isDiscovering ? 'Suche…' : 'Kalender suchen';
    return isCreatingMany
      ? 'Speichere…'
      : `${Object.values(selected).filter(Boolean).length} Kalender hinzufügen`;
  })();

  const submitDisabled =
    submitting || (provider === 'o365' && o365Phase === 'login');

  const handleSubmit = () => {
    if (provider === 'o365') {
      if (o365Phase === 'intro') return void startO365Login();
      if (o365Phase === 'login') return; // disabled
      return void submitO365();
    }
    return void submitWebDAV();
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Kalender hinzufügen</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label='Anbieter'
            value={provider}
            onChange={e =>
              handleProviderChange(e.target.value as CalendarProvider)
            }
            disabled={Boolean(discovered) || o365Phase === 'login'}
          >
            <MenuItem value='o365'>Microsoft 365</MenuItem>
            <MenuItem value='webdav'>WebDAV / CalDAV</MenuItem>
          </TextField>

          {provider === 'o365' && o365Phase === 'intro' && (
            <Alert severity='info'>
              Klicken Sie auf <strong>„Mit Microsoft anmelden"</strong>. Es
              öffnet sich die Microsoft-Anmeldung. Nach erfolgreicher
              Authentifizierung lädt DiNo automatisch die verfügbaren Kalender —
              hier wählen Sie aus, welche Sie in DiNo einbinden möchten.
              <br />
              <br />
              Voraussetzung: die Erweiterung „Calendar" ist konfiguriert
              (Einstellungen → Erweiterungen → Calendar → Konfigurieren).
            </Alert>
          )}

          {provider === 'o365' && o365Phase === 'login' && (
            <Alert severity='info' icon={<CircularProgress size={18} />}>
              Bitte schließen Sie die Microsoft-Anmeldung im Popup-Fenster ab.
              Sobald die Authentifizierung fertig ist, werden Ihre Kalender hier
              angezeigt.
            </Alert>
          )}

          {provider === 'o365' &&
            o365Phase === 'choose' &&
            discovered &&
            discovered.length > 0 && (
              <>
                <Alert severity='success'>
                  Microsoft-Anmeldung erfolgreich. {discovered.length} Kalender
                  gefunden. Bitte auswählen, welche in DiNo eingebunden werden
                  sollen:
                </Alert>
                <Box
                  sx={{
                    maxHeight: 280,
                    overflowY: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {discovered.map(c => {
                    const key = c.id || c.url;
                    return (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            checked={Boolean(selected[key])}
                            onChange={e =>
                              setSelected(prev => ({
                                ...prev,
                                [key]: e.target.checked,
                              }))
                            }
                          />
                        }
                        label={
                          <Stack>
                            <Typography variant='body2'>
                              {c.color && (
                                <Box
                                  component='span'
                                  sx={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    background: c.color,
                                    mr: 1,
                                    verticalAlign: 'middle',
                                  }}
                                />
                              )}
                              {c.displayName}
                              {c.isDefault && (
                                <Chip
                                  size='small'
                                  label='Standard'
                                  color='warning'
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                            {c.ownerEmail && (
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                {c.ownerEmail}
                              </Typography>
                            )}
                          </Stack>
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          mb: 0.5,
                        }}
                      />
                    );
                  })}
                </Box>
              </>
            )}

          {isWebDAV && !discovered && (
            <>
              <TextField
                label='Server-URL'
                placeholder='https://cloud.example/remote.php/dav/'
                helperText='Es genügt die DAV-Wurzel des Servers oder Ihr Principal-Pfad — die zugehörigen Kalender werden automatisch ermittelt.'
                value={config.url ?? ''}
                onChange={e => setConfig({ ...config, url: e.target.value })}
              />
              <TextField
                label='Benutzername'
                value={config.username ?? ''}
                onChange={e =>
                  setConfig({ ...config, username: e.target.value })
                }
              />
              <TextField
                label='Passwort'
                type='password'
                value={config.password ?? ''}
                onChange={e =>
                  setConfig({ ...config, password: e.target.value })
                }
              />
            </>
          )}

          {isWebDAV && discovered && (
            <>
              <Alert severity='success'>
                {discovered.length} Kalender gefunden. Bitte auswählen, welche
                hinzugefügt werden sollen:
              </Alert>
              <Box
                sx={{
                  maxHeight: 280,
                  overflowY: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {discovered.map(c => (
                  <FormControlLabel
                    key={c.url}
                    control={
                      <Checkbox
                        checked={Boolean(selected[c.url])}
                        onChange={e =>
                          setSelected(prev => ({
                            ...prev,
                            [c.url]: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={
                      <Stack>
                        <Typography variant='body2'>
                          {c.color && (
                            <Box
                              component='span'
                              sx={{
                                display: 'inline-block',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: c.color,
                                mr: 1,
                                verticalAlign: 'middle',
                              }}
                            />
                          )}
                          {c.displayName}
                        </Typography>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {c.url}
                        </Typography>
                      </Stack>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}
                  />
                ))}
              </Box>
              <Button
                size='small'
                onClick={() => {
                  setDiscovered(null);
                  setSelected({});
                }}
              >
                ← Andere Zugangsdaten eingeben
              </Button>
            </>
          )}

          {error && <Alert severity='error'>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbruch</Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={submitDisabled}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
